package org.example.project.enginetabular

import org.example.project.dashboard.DashboardTheme

import org.example.project.dashboard.AppDialogs

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import org.example.project.dashboard.POPUP_ENGINE_MODAL_Z_INDEX
import org.example.project.dashboard.PopupEngineCard
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.UISettings
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

private const val NAV_ICON_FILTER_ACTIVE = UISettings.ICON_FILTER_ACCENT

@Composable
fun CsvSettingsPopup(
    allHeaders: List<String>,
    headerDisplayLabels: Map<String, String> = emptyMap(),
    columnOrder: List<String>,
    columnVisibility: Map<String, Boolean>,
    onColumnToggleVisibilityChange: (header: String, isChecked: Boolean) -> Unit,
    csvImportProfiles: Map<String, Map<String, String>>,
    selectedCsvImportProfile: String?,
    onCsvImportProfileSelect: (String?) -> Unit,
    onCsvImportProfileSave: (String, Map<String, String>) -> Unit,
    onCsvImportProfileDelete: (String) -> Unit,
    csvExportProfiles: Map<String, Map<String, String>>,
    selectedCsvExportProfile: String?,
    onCsvExportProfileSelect: (String?) -> Unit,
    onCsvExportProfileSave: (String, Map<String, String>) -> Unit,
    onCsvExportProfileDelete: (String) -> Unit,
    filterToggleHeaders: Set<String>,
    filterProfiles: Map<String, Map<String, String>>,
    selectedFilterProfile: String?,
    onFilterProfileSelect: (String?) -> Unit,
    onFilterProfileSave: (String, Map<String, String>) -> Unit,
    onFilterProfileDelete: (String) -> Unit,
    onClose: () -> Unit
) {
    fun displayHeader(header: String) = headerDisplayLabels[header]?.takeIf { it.isNotBlank() } ?: header
    fun savedValue(profile: Map<String, String>?, header: String) = profile?.get(header)
        ?: profile?.get(displayHeader(header)).orEmpty()
    var selectedImportProfileLocal by remember(selectedCsvImportProfile) { mutableStateOf(selectedCsvImportProfile.orEmpty()) }
    var draftImportMapping by remember(selectedCsvImportProfile, csvImportProfiles, allHeaders) {
        mutableStateOf(
            allHeaders.associateWith { header ->
                selectedCsvImportProfile?.let { savedValue(csvImportProfiles[it], header) }.orEmpty()
            }.toMutableMap()
        )
    }

    var selectedExportProfileLocal by remember(selectedCsvExportProfile) { mutableStateOf(selectedCsvExportProfile.orEmpty()) }
    var draftExportMapping by remember(selectedCsvExportProfile, csvExportProfiles, allHeaders) {
        mutableStateOf(
            allHeaders.associateWith { header ->
                selectedCsvExportProfile?.let { savedValue(csvExportProfiles[it], header) }.orEmpty()
            }.toMutableMap()
        )
    }

    var selectedFilterProfileLocal by remember(selectedFilterProfile) { mutableStateOf(selectedFilterProfile.orEmpty()) }
    var draftFilterMapping by remember(selectedFilterProfile, filterProfiles, allHeaders) {
        mutableStateOf(
            allHeaders.associateWith { header ->
                selectedFilterProfile?.let { savedValue(filterProfiles[it], header) }.orEmpty()
            }.toMutableMap()
        )
    }

    var settingsMode by remember { mutableStateOf("home") }

    val isExport = settingsMode == "export"
    val isFilter = settingsMode == "filter"
    val profileSet = when {
        isFilter -> filterProfiles
        isExport -> csvExportProfiles
        else -> csvImportProfiles
    }
    val selectedProfile = when {
        isFilter -> selectedFilterProfileLocal
        isExport -> selectedExportProfileLocal
        else -> selectedImportProfileLocal
    }
    val draftMap = when {
        isFilter -> draftFilterMapping
        isExport -> draftExportMapping
        else -> draftImportMapping
    }

    PopupEngineCard(
        windowKey = "csv-settings-popup",
        zIndex = POPUP_ENGINE_MODAL_Z_INDEX,
        onClose = onClose
    ) {
        Div({
            style {
                position(Position.Relative)
                flexGrow(1)
                minHeight(0.px)
                overflow("hidden")
                padding(14.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Center)
                gap(10.px)
            }
        }) {
            when (settingsMode) {
                "home" -> {
                    Div({
                        style {
                            width(100.percent)
                            flexGrow(1)
                            minHeight(0.px)
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                            justifyContent(JustifyContent.Center)
                            alignItems(AlignItems.Center)
                            gap(28.px)
                        }
                    }) {
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                justifyContent(JustifyContent.Center)
                                gap(28.px)
                                flexWrap(FlexWrap.Wrap)
                            }
                        }) {
                            CsvEntryCard("CSV Import", "images/upload.svg") {
                                settingsMode = "import"
                            }

                            CsvEntryCard("CSV Export", "images/download.svg") {
                                settingsMode = "export"
                            }

                            CsvEntryCard("Filterprofile", "images/filter_list_off.svg") {
                                settingsMode = "filter"
                            }

                            CsvEntryCard("Spalten", "images/view_column.svg") {
                                settingsMode = "columns"
                            }
                        }
                    }
                }
                "columns" -> {
                    BackToSettingsButton { settingsMode = "home" }
                    Div({
                        style {
                            width(100.percent)
                            flexGrow(1)
                            minHeight(0.px)
                            display(DisplayStyle.Flex)
                            justifyContent(JustifyContent.Center)
                            alignItems(AlignItems.Center)
                        }
                    }) {
                        Div({
                            style {
                                width(100.percent)
                                maxWidth(900.px)
                                maxHeight(100.percent)
                                overflowY("auto")
                                display(DisplayStyle.Grid)
                                gridTemplateColumns("repeat(3, minmax(0, 1fr))")
                                gap(10.px)
                                alignContent(AlignContent.Start)
                                padding(2.px, 10.px, 8.px, 10.px)
                                property("box-sizing", "border-box")
                            }
                        }) {
                            columnOrder.forEach { header ->
                                val isVisible = columnVisibility[header] ?: true
                                Div({
                                    style {
                                        display(DisplayStyle.Flex)
                                        alignItems(AlignItems.Center)
                                        gap(8.px)
                                        padding(8.px, 10.px)
                                        borderRadius(8.px)
                                        backgroundColor(Color("#171a20"))
                                        border(1.px, LineStyle.Solid, Color("#303645"))
                                        cursor("pointer")
                                    }
                                    onClick { onColumnToggleVisibilityChange(header, !isVisible) }
                                }) {
                                    Span({ style { fontSize(16.px); property("user-select", "none") } }) {
                                        Text(if (isVisible) "\u2705" else "\u2B1C")
                                    }
                                    Span({ style { color(Color.white); fontSize(12.px) } }) { Text(displayHeader(header)) }
                                }
                            }
                        }
                    }
                }
                else -> {
                    BackToSettingsButton { settingsMode = "home" }
                    Div({
                        style {
                            width(100.percent)
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(8.px)
                            padding(8.px)
                            marginBottom(6.px)
                            property("box-sizing", "border-box")
                        }
                    }) {
                        CsvNavButton("Erstellen", "images/add_circle.svg") {
                            val nextIndex = (profileSet.keys.mapNotNull { key ->
                                key.removePrefix("Profil ").toIntOrNull()
                            }.maxOrNull() ?: 0) + 1
                            val newName = "Profil $nextIndex"
                            val defaultMapping = when {
                                isFilter -> allHeaders.associateWith { header -> draftFilterMapping[header].orEmpty() }
                                else -> allHeaders.associateWith { displayHeader(it) }
                            }
                            when {
                                isFilter -> {
                                    onFilterProfileSave(newName, defaultMapping)
                                    onFilterProfileSelect(newName)
                                    selectedFilterProfileLocal = newName
                                    draftFilterMapping = allHeaders.associateWith { header -> defaultMapping[header].orEmpty() }.toMutableMap()
                                }
                                isExport -> {
                                    onCsvExportProfileSave(newName, defaultMapping)
                                    onCsvExportProfileSelect(newName)
                                    selectedExportProfileLocal = newName
                                    draftExportMapping = allHeaders.associateWith { displayHeader(it) }.toMutableMap()
                                }
                                else -> {
                                    onCsvImportProfileSave(newName, defaultMapping)
                                    onCsvImportProfileSelect(newName)
                                    selectedImportProfileLocal = newName
                                    draftImportMapping = allHeaders.associateWith { displayHeader(it) }.toMutableMap()
                                }
                            }
                        }

                        CsvNavButton("Laden", "images/download.svg") {
                            val profileName = selectedProfile
                            if (profileName.isBlank()) {
                                AppDialogs.alert("Bitte Profil auswählen.")
                                return@CsvNavButton
                            }
                            when {
                                isFilter -> {
                                    draftFilterMapping = allHeaders.associateWith { h -> savedValue(filterProfiles[profileName], h) }.toMutableMap()
                                    onFilterProfileSelect(profileName)
                                    selectedFilterProfileLocal = profileName
                                }
                                isExport -> {
                                    draftExportMapping = allHeaders.associateWith { h -> savedValue(csvExportProfiles[profileName], h) }.toMutableMap()
                                    onCsvExportProfileSelect(profileName)
                                    selectedExportProfileLocal = profileName
                                }
                                else -> {
                                    draftImportMapping = allHeaders.associateWith { h -> savedValue(csvImportProfiles[profileName], h) }.toMutableMap()
                                    onCsvImportProfileSelect(profileName)
                                    selectedImportProfileLocal = profileName
                                }
                            }
                        }

                        CsvNavButton("Speichern", "images/save.svg") {
                            val profileName = selectedProfile
                            if (profileName.isBlank()) {
                                AppDialogs.alert("Bitte Profil auswählen.")
                                return@CsvNavButton
                            }
                            val normalized = allHeaders.associateWith { h -> draftMap[h].orEmpty() }
                            when {
                                isFilter -> {
                                    onFilterProfileSave(profileName, normalized)
                                    onFilterProfileSelect(profileName)
                                    selectedFilterProfileLocal = profileName
                                }
                                isExport -> {
                                    onCsvExportProfileSave(profileName, normalized)
                                    onCsvExportProfileSelect(profileName)
                                    selectedExportProfileLocal = profileName
                                }
                                else -> {
                                    onCsvImportProfileSave(profileName, normalized)
                                    onCsvImportProfileSelect(profileName)
                                    selectedImportProfileLocal = profileName
                                }
                            }
                        }

                        CsvNavButton("Löschen", "images/delete.svg", showDivider = false) {
                            val profileName = selectedProfile
                            if (profileName.isBlank()) {
                                AppDialogs.alert("Bitte Profil auswählen.")
                                return@CsvNavButton
                            }
                            AppDialogs.confirm("Profil '$profileName' wirklich löschen?") {
                                when {
                                    isFilter -> {
                                        onFilterProfileDelete(profileName)
                                        if (selectedFilterProfileLocal == profileName) selectedFilterProfileLocal = ""
                                    }
                                    isExport -> {
                                        onCsvExportProfileDelete(profileName)
                                        if (selectedExportProfileLocal == profileName) selectedExportProfileLocal = ""
                                    }
                                    else -> {
                                        onCsvImportProfileDelete(profileName)
                                        if (selectedImportProfileLocal == profileName) selectedImportProfileLocal = ""
                                    }
                                }
                            }
                        }
                    }

                    Div({
                        style {
                            width(100.percent)
                            maxWidth(420.px)
                            marginTop(8.px)
                            marginBottom(10.px)
                        }
                    }) {
                        AppDropdown(
                            options = buildList {
                                add(All(key = "", label = "Profil auswählen..."))
                                addAll(profileSet.keys.sortedBy { it.lowercase() }.map { All(key = it, label = it) })
                            },
                            selectedValue = selectedProfile,
                            appearance = AppDropdownAppearance.Field,
                            onChange = { name ->
                                when {
                                    isFilter -> {
                                        draftFilterMapping = allHeaders.associateWith { h -> savedValue(filterProfiles[name], h) }.toMutableMap()
                                        selectedFilterProfileLocal = name
                                        onFilterProfileSelect(name.ifBlank { null })
                                    }
                                    isExport -> {
                                        draftExportMapping = allHeaders.associateWith { h -> savedValue(csvExportProfiles[name], h) }.toMutableMap()
                                        selectedExportProfileLocal = name
                                        onCsvExportProfileSelect(name.ifBlank { null })
                                    }
                                    else -> {
                                        draftImportMapping = allHeaders.associateWith { h -> savedValue(csvImportProfiles[name], h) }.toMutableMap()
                                        selectedImportProfileLocal = name
                                        onCsvImportProfileSelect(name.ifBlank { null })
                                    }
                                }
                            },
                            attrs = {
                                classes(TableStyles.input, TableStyles.select)
                                style {
                                    width(100.percent)
                                    height(36.px)
                                    backgroundColor(Color("#171a20"))
                                    border(1.px, LineStyle.Solid, Color("#303645"))
                                    borderRadius(6.px)
                                    padding(0.px, 10.px)
                                    color(Color.white)
                                    textAlign("center")
                                }
                            }
                        )
                    }

                    Div({
                        style {
                            width(100.percent)
                            maxWidth(900.px)
                            flexGrow(1)
                            minHeight(0.px)
                            overflowY("auto")
                            padding(2.px, 10.px, 8.px, 10.px)
                            property("box-sizing", "border-box")
                        }
                    }) {
                        Div({
                            style {
                                display(DisplayStyle.Grid)
                                gridTemplateColumns("minmax(0, 1fr) minmax(0, 1fr)")
                                columnGap(16.px)
                                rowGap(8.px)
                                alignItems(AlignItems.Center)
                            }
                        }) {
                            Span({ style { color(Color("#d7deea")); fontSize(12.px); fontWeight("700") } }) {
                                Text(if (isFilter) "Header" else "Original")
                            }
                            Span({ style { color(Color("#d7deea")); fontSize(12.px); fontWeight("700") } }) {
                                Text(if (isFilter) "Filterwert" else "Alias")
                            }

                            allHeaders.forEach { header ->
                                Input(InputType.Text, attrs = {
                                    value(displayHeader(header))
                                    attr("readonly", "true")
                                    style {
                                        width(100.percent)
                                        height(32.px)
                                        backgroundColor(Color("#0f1218"))
                                        border(1.px, LineStyle.Solid, Color("#303645"))
                                        borderRadius(6.px)
                                        padding(6.px)
                                        color(Color("#d7deea"))
                                        property("box-sizing", "border-box")
                                    }
                                })

                                if (isFilter && header in filterToggleHeaders) {
                                    AppDropdown(
                                        options = listOf(
                                            All(key = "", label = ""),
                                            All(key = "true", label = "true"),
                                            All(key = "false", label = "false")
                                        ),
                                        selectedValue = draftMap[header].orEmpty(),
                                        appearance = AppDropdownAppearance.Field,
                                        onChange = {
                                            draftFilterMapping = draftFilterMapping.toMutableMap().apply { put(header, it) }
                                        },
                                        attrs = {
                                            classes(TableStyles.input, TableStyles.select)
                                            style {
                                                width(100.percent)
                                                height(32.px)
                                                backgroundColor(Color("#0f1218"))
                                                border(1.px, LineStyle.Solid, Color("#303645"))
                                                borderRadius(6.px)
                                                padding(0.px, 6.px)
                                                color(Color.white)
                                                property("box-sizing", "border-box")
                                            }
                                        }
                                    )
                                } else {
                                    Input(InputType.Text, attrs = {
                                        value(draftMap[header].orEmpty())
                                        attr("placeholder", if (isFilter) "" else "Alias")
                                        attr("spellcheck", "false")
                                        onInput {
                                            when {
                                                isFilter -> {
                                                    draftFilterMapping = draftFilterMapping.toMutableMap().apply { put(header, it.value) }
                                                }
                                                isExport -> {
                                                    draftExportMapping = draftExportMapping.toMutableMap().apply { put(header, it.value) }
                                                }
                                                else -> {
                                                    draftImportMapping = draftImportMapping.toMutableMap().apply { put(header, it.value) }
                                                }
                                            }
                                        }
                                        style {
                                            width(100.percent)
                                            height(32.px)
                                            backgroundColor(Color("#0f1218"))
                                            border(1.px, LineStyle.Solid, Color("#303645"))
                                            borderRadius(6.px)
                                            padding(6.px)
                                            color(Color.white)
                                            property("-webkit-text-fill-color", "#ffffff")
                                            property("caret-color", "#ffffff")
                                            opacity(1)
                                            outline("none")
                                            cursor("text")
                                            property("box-sizing", "border-box")
                                        }
                                    })
                                }
                            }
                        }
                    }
                }
            }
        }

    }
}

@Composable
private fun BackToSettingsButton(
    onClick: () -> Unit
) {
    Div({
        style {
            width(100.percent)
            maxWidth(900.px)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.FlexStart)
            alignItems(AlignItems.Center)
            padding(0.px, 10.px)
            marginBottom(4.px)
            property("box-sizing", "border-box")
            opacity(0.6)
            cursor("pointer")
        }
        onClick { onClick() }
    }) {
        Img(src = "images/back.svg", attrs = {
            style {
                width(28.px)
                height(28.px)
                property("filter", "brightness(0) invert(1)")
            }
        })
    }
}

@Composable
private fun CsvEntryCard(
    title: String,
    icon: String,
    onClick: () -> Unit
) {
    var hover by remember { mutableStateOf(false) }
    Div({
        style {
            width(185.px)
            padding(30.px, 18.px)
            borderRadius(16.px)
            backgroundColor(DashboardTheme.SURFACE_CARD)
            property("border", "1px solid rgba(255,255,255,0.16)")
            cursor("pointer")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            gap(18.px)
        }
        onMouseEnter { hover = true }
        onMouseLeave { hover = false }
        onClick { onClick() }
    }) {
        Img(src = icon, attrs = {
            style {
                width(42.px)
                height(42.px)
                property(
                    "filter",
                    if (hover) UISettings.ICON_FILTER_ACCENT
                    else "brightness(0) invert(1)"
                )
            }
        })
        Span({ style { fontWeight("600"); fontSize(15.px); color(Color.white) } }) {
            Text(title)
        }
    }
}

@Composable
private fun CsvNavButton(
    label: String,
    icon: String,
    showDivider: Boolean = true,
    onClick: () -> Unit
) {
    var hovered by remember { mutableStateOf(false) }

    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
        }
    }) {
        Button(attrs = {
            classes(TableStyles.topBarButton)
            style {
                height(100.percent)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(8.px)
            }
            onMouseEnter { hovered = true }
            onMouseLeave { hovered = false }
            onClick { onClick() }
        }) {
            Img(src = icon, attrs = {
                classes(TableStyles.topBarIcon)
                style {
                    property(
                        "filter",
                        if (hovered) NAV_ICON_FILTER_ACTIVE else "brightness(1)"
                    )
                }
            })
            Span({
                style {
                    if (hovered) color(UISettings.ACCENT)
                    whiteSpace("nowrap")
                }
            }) {
                Text(label)
            }
        }

        if (showDivider) {
            Div({ classes(TableStyles.quickDivider) })
        }
    }
}
